/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import trI18n from '@/common/utils/trI18n';
import { tr } from '@/common/utils/tr';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { setPanelSelector, closePanelTabSelector } from '@/state/selector/panelSelector';
import './AiAutoLog.css';

import Constant from '@/common/constant/Constant';
import AiAutoConstant from '@/common/constant/AiAutoConstant';
import Utils from '@/common/utils/utils';
import { formatUserDateTime } from '@/common/utils/timezoneUtils';
import { showLoadingMouse, hideLoadingMouse } from '@/common/utils/CustomCursor';
import { getAiOperatorPromptLog } from '@/component/ticketKnowledge/handler/DetailTicketHandler';
import { updateAiAutoLog } from '@/service/aiAutoLogService';
import { getTicketById } from '@/service/ticketService';

import CircularProgress from '@material-ui/core/CircularProgress';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { Button } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { PropTypes } from 'prop-types';
import { isLimitedTabsLength } from '@/service/userService';
import { multiPanelAtom } from '@/state/atom/panelAtom';
import { projectInuseDetailAtom } from '@/state/atom/projectAtom';
import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';

TelephonePromptLog.propTypes = {
    corporationDetail: PropTypes.any,
    selectedProject: PropTypes.any,
    currentUserDetail: PropTypes.any,
    ticketDetail: PropTypes.any,
    panelId: PropTypes.any,
    isDisabled: PropTypes.any
};

function TelephonePromptLog({
    corporationDetail,
    selectedProject,
    currentUserDetail,
    ticketDetail,
    panelId,
    isDisabled
}) {
    // Re-render when the translation dictionary loads/updates
    useTranslationSubscription();

    const [isExpand, setIsExpand] = useState(false);
    const [listLog, setListLog] = useState([]);
    const [isShowSeeMore, setIsShowSeeMore] = useState(false);
    const [isLoadingGetLog, setIsLoadingGetLog] = useState(false);
    const [errorMessagePopup, setErrorMessagePopup] = useState({ isShow: false });
    const [
        isAutoDeleteLogSettingTelephonePrompt,
        setIsAutoDeleteLogSettingTelephonePrompt
    ] = useState(true);
    const panelList = useRecoilValue(multiPanelAtom);
    const projectInuseDetail = useRecoilValue(projectInuseDetailAtom);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const addNewPanel = useSetRecoilState(setPanelSelector);
    const closePanelTab = useSetRecoilState(closePanelTabSelector);

    useEffect(() => {
        let promptData = Utils.convertJSONDataToObject(ticketDetail.promptData);
        let autoDeleteLog = true;
        if (!Utils.isEmptyObject(promptData)) {
            if (typeof promptData.autoDeleteLog === 'boolean') {
                autoDeleteLog = promptData.autoDeleteLog;
            }
        }

        setIsAutoDeleteLogSettingTelephonePrompt(autoDeleteLog);
    }, []);

    const handleReloadLogList = async () => {
        setIsLoadingGetLog(true);
        const args = {
            corporationDetail,
            selectedProject,
            currentUserDetail,
            limit:
                listLog.length < AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT
                    ? AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT
                    : listLog.length
        };
        const { listLog: reloadList, error, lastEvaluatedKey } = await getAiOperatorPromptLog(
            args,
            ticketDetail.id
        );
        if (!Utils.isEmptyString(error)) {
            setErrorMessagePopup({ isShow: true, msgCode: error });
            setIsLoadingGetLog(false);
            setIsShowSeeMore(false);
            return;
        }
        setListLog(reloadList);
        setIsShowSeeMore(!Utils.isEmptyObject(lastEvaluatedKey));
        setIsLoadingGetLog(false);
    };

    const handleToggleAutoDeleteLogTelephonePrompt = async () => {
        if (isDisabled) return;
        try {
            setIsAutoDeleteLogSettingTelephonePrompt(!isAutoDeleteLogSettingTelephonePrompt);
            let requestDto = {
                ticketId: ticketDetail.id,
                aiChatThreadId: 'fake',
                actionType:
                    AiAutoConstant.THREAD_LOG_UPDATE_API.UPDATE_AUTO_DELETE_OPERATOR_LOG_FLAG,
                userId: currentUserDetail.id,
                corporationInuse: corporationDetail.id,
                projectInuse: selectedProject.id
            };

            let response = await updateAiAutoLog(requestDto);
            if (response.status === false) {
                hideLoadingMouse();
                setErrorMessagePopup({
                    isShow: true,
                    msgCode: response.errorCode
                });
                setIsAutoDeleteLogSettingTelephonePrompt(!isAutoDeleteLogSettingTelephonePrompt);
                return;
            }
        } catch (error) {
            console.log('handleToggleAutoDeleteLogTelephonePrompt error:', error);
        }
    };

    const handleSeeMore = async () => {
        try {
            showLoadingMouse();
            const currentLastLog = listLog[listLog.length - 1];
            const args = {
                corporationDetail,
                selectedProject,
                currentUserDetail,
                lastEvaluatedKey: {
                    id: { S: currentLastLog.id },
                    createdAt: { S: currentLastLog.createdAt },
                    ticketId: { S: currentLastLog.ticketId }
                },
                limit: AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT
            };
            const { listLog: getMoreList, error, lastEvaluatedKey } = await getAiOperatorPromptLog(
                args,
                ticketDetail.id
            );
            if (!Utils.isEmptyString(error)) {
                setErrorMessagePopup({ isShow: true, msgCode: error });
                return;
            }
            setListLog(prev => [...prev, ...getMoreList]);
            setIsShowSeeMore(!Utils.isEmptyObject(lastEvaluatedKey));
            hideLoadingMouse();
        } catch (error) {
            console.log('TelephonePromptLog handleSeeMore error:', error);
            hideLoadingMouse();
        }
    };

    const handleOpenTabLog = async logDetail => {
        if (!logDetail) return;
        const existTicket = await getTicketById(null, ticketDetail.id);
        if (!existTicket) {
            setErrorMessagePopup({
                isShow: true,
                msgCode: Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR
            });
            return;
        }

        const panelDataChatAI = panelList?.find(
            panel => panel?.type === Constant.PANEL_TYPE.OPEN_CHAT_AI
        );
        const isLimited = await isLimitedTabsLength({
            panelDataChatAI,
            projectInuseDetail,
            userDetail: currentUserDetail
        });

        if (isLimited) {
            enqueueSnackbar(buildSnackbarMessage('MSG-CM-WARNING-LIMIT-TAB'), {
                variant: 'error',
                autoHideDuration: 3000,
                style: { whiteSpace: 'pre-line' }
            });
            return;
        }

        showLoadingMouse();
        setTimeout(() => {
            const isAiAssistantLog = ticketDetail?.formDetail?.definitionVariant === Constant.FORM_VARIANT.AI_ASSISTANT_PROMPT;
            addNewPanel({
                type: Constant.PANEL_TYPE.OPEN_CHAT_AI,
                tabId: logDetail.chatThreadId,
                currentPanelId: panelId,
                filterKnowledgeListData: {
                    resetSearchCondition: false,
                    usingBasicPrompt: true,
                    isInvalidPrecondition: false,
                    threadId: logDetail.chatThreadId,
                    projectSelectedId: selectedProject.id,
                    aiAutoLog: logDetail,
                    isOperatorPromptLog: !isAiAssistantLog,
                    isAiAssistantLog: isAiAssistantLog
                }
            });
            hideLoadingMouse();
        }, 1000);
    };

    const handleDeleteLog = async logDetail => {
        if (isDisabled) return;
        try {
            showLoadingMouse();
            const requestDto = {
                ticketId: ticketDetail.id,
                aiChatThreadId: logDetail.id,
                actionType: AiAutoConstant.THREAD_LOG_UPDATE_API.DELETE_OPERATOR_LOG,
                userId: currentUserDetail.id,
                corporationInuse: corporationDetail.id,
                projectInuse: selectedProject.id
            };
            const response = await updateAiAutoLog(requestDto);
            const handledLogStatus = response?.status;
            if (!handledLogStatus) {
                hideLoadingMouse();
                setErrorMessagePopup({ isShow: true, msgCode: response?.errorCode });
                return;
            }

            const nextList = listLog.filter(log => log.id !== logDetail.id);
            setListLog(nextList);
            if (
                Utils.isEmptyArray(nextList) ||
                nextList.length < AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT
            ) {
                setIsShowSeeMore(false);
            }

            closePanelTab({
                event: null,
                panelType: Constant.PANEL_TYPE.OPEN_CHAT_AI,
                panelId: panelId,
                tabId: logDetail.chatThreadId
            });
            hideLoadingMouse();
        } catch (error) {
            console.log('TelephonePromptLog handleDeleteLog error:', error);
            hideLoadingMouse();
        }
    };

    const renderLogStatus = status => {
        if (!status) return <></>;
        switch (status) {
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.RUNNING:
                return <></>;
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSED:
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSING:
                return <img src="/assets/image/status-stop.svg" alt="status-stop" />;
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.DONE:
                return <img src="/assets/image/status-success.svg" alt="status-success" />;
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.ERROR:
                return <img src="/assets/image/status-error.svg" alt="status-error" />;
            default:
                return <></>;
        }
    };

    const renderLogTimer = log => {
        if (!log) return '';

        const startTime = log.startTime ? formatUserDateTime(log.startTime, 'YYYY/MM/DD HH:mm', currentUserDetail?.userSetting) : '';
        const endTime = log.endTime ? formatUserDateTime(log.endTime, 'YYYY/MM/DD HH:mm', currentUserDetail?.userSetting) : '';
        const updatedTime = formatUserDateTime(log.updatedAt, 'YYYY/MM/DD HH:mm', currentUserDetail?.userSetting);

        let endStatus = '';
        switch (log.status) {
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.RUNNING:
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSING:
                endStatus = `${tr('RUNNING')}...`;
                break;
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSED:
                endStatus = `${tr('STOP')} ${updatedTime}`;
                break;
            default:
                endStatus = `${tr('END')} ${endTime}`;
                break;
        }

        return `${tr('START')} ${startTime}  ${endStatus}`;
    };

    const handleExpandLog = async () => {
        if (!isExpand && Utils.isEmptyArray(listLog)) {
            setIsLoadingGetLog(true);
            showLoadingMouse();
            try {
                const args = {
                    corporationDetail,
                    selectedProject,
                    currentUserDetail,
                    limit: AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT
                };
                const { listLog: initList, error, lastEvaluatedKey } = await getAiOperatorPromptLog(
                    args,
                    ticketDetail.id
                );
                if (!Utils.isEmptyString(error)) {
                    setErrorMessagePopup({ isShow: true, msgCode: error });
                    setIsLoadingGetLog(false);
                    hideLoadingMouse();
                    return;
                }
                setListLog(initList);
                setIsShowSeeMore(!Utils.isEmptyObject(lastEvaluatedKey));
            } catch (error) {
                console.error('TelephonePromptLog init fetch error:', error);
            }
            setIsLoadingGetLog(false);
            hideLoadingMouse();
        }
        setIsExpand(prev => !prev);
    };

    return (
        <SnackbarProvider
            maxSnack={10}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            action={key => (
                <Button className="notistack-button" onClick={() => closeSnackbar(key)}>
                    <CloseIcon />
                </Button>
            )}
        >
            <div
                className="accordionHead-SettingTitle"
                onClick={async () => await handleExpandLog()}
            >
                <img src="/assets/image/execute-log.svg" alt="execute-log.svg" />
                <span className="text">{trI18n('EXECUTION_LOG')}</span>
                {isExpand ? (
                    <img src="/assets/image/icon-minus-accordion.svg" alt="minus" />
                ) : (
                    <img src="/assets/image/icon-plus-accordion.svg" alt="plus" />
                )}
            </div>

            {isLoadingGetLog ? (
                <div className="ai-auto-log-inner ai-log-flex justify-center">
                    <CircularProgress color="primary" size="1.4rem" />
                </div>
            ) : isExpand ? (
                <div className="ai-auto-log-inner">
                    <div className="ai-auto-log-sub-header ai-log-flex justify-between">
                        <div className="ai-log-flex justify-between gap-4">
                            <span className="ai-log-text f-weight-700">
                                {trI18n('EXECUTION_LOG')}
                            </span>
                            <button
                                onClick={async () => await handleReloadLogList()}
                                className="btn-reset-default ai-log-btn"
                            >
                                <img src="/assets/image/reload-icon.svg" alt="reload-icon" />
                            </button>
                        </div>
                        <div className="ai-log-flex justify-between gap-4">
                            <span className="ai-log-text f-weight-500">
                                {trI18n('AI_AUTO_DELETE_LOG_SUBSCRIPTION')}
                            </span>
                            <div className="switch-check">
                                <input
                                    name="sdauto-delete-log-toggle"
                                    id="sdauto-delete-log-toggle"
                                    type="checkbox"
                                    checked={isAutoDeleteLogSettingTelephonePrompt}
                                    disabled={isDisabled}
                                    onChange={e => {
                                        handleToggleAutoDeleteLogTelephonePrompt();
                                    }}
                                />
                                <label htmlFor="sdauto-delete-log-toggle"></label>
                            </div>
                        </div>
                    </div>
                    <div className="ai-log-flex vertical gap-8 ai-log-list">
                        {listLog.length > 0 ? (
                            listLog.map(log => {
                                return (
                                    <div className="log-ai-detail-wrapper ai-log-flex" key={log.id}>
                                        <div className="log-ai-status">
                                            {renderLogStatus(log?.status)}
                                        </div>
                                        <button
                                            className="btn-reset-default log-ai-runtime ai-log-text"
                                            onClick={async () => await handleOpenTabLog(log)}
                                        >
                                            {renderLogTimer(log)}
                                        </button>
                                        {log.status !==
                                            AiAutoConstant.AICHATTHREAD_LOG_STATUS.RUNNING && !isDisabled && (
                                            <div className="log-ai-action ai-log-flex justify-end gap-8">
                                                <button
                                                    onClick={async () => await handleDeleteLog(log)}
                                                    className="btn-reset-default ai-log-btn"
                                                >
                                                    <img
                                                        src="/assets/image/icon-trash.svg"
                                                        alt="icon-trash"
                                                    />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <p style={{ margin: '0 auto' }}>{trI18n('EMPTY_EXECUTION_LOG')}</p>
                        )}
                    </div>
                    <div className="ai-log-footer ai-log-flex justify-end">
                        {isShowSeeMore && (
                            <button
                                onClick={async () => await handleSeeMore()}
                                className="btn-reset-default ai-log-see-more"
                            >
                                {trI18n('COMBTN009')}
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <></>
            )}

            <ConfirmDialog
                isOpen={errorMessagePopup.isShow}
                msgConfirm={trI18n(errorMessagePopup?.msgCode || 'MSG-AI-LOG-CHANGED-DATA')}
                textSubmit={tr('OK')}
                okCallBack={() => {
                    setErrorMessagePopup({ ...errorMessagePopup, isShow: false });
                    window.location.reload();
                }}
            />
        </SnackbarProvider>
    );
}

export default TelephonePromptLog;
